import { motion } from "framer-motion";
import { Instagram, Facebook, Lock } from "lucide-react";
import { T } from "../data/content";
import { useLang } from "../context/LangContext";
import { useSiteData } from "../context/SiteDataContext";
import { WhatsAppIcon, Phone, TikTokIcon } from "./icons";
import { BookingQR } from "./BookingQR";

export const Footer = () => {
  const { tr } = useLang();
  const { brand } = useSiteData();
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-[#0C0806] pt-24" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="flex flex-col items-center gap-8 border-b border-white/10 pb-20 text-center"
        >
          <img src={brand.logo} alt="Slaly Hair & Beauty logo" className="h-24 w-auto rounded-xl bg-[#F5ECDA] p-2.5 object-contain shadow-[0_20px_40px_-15px_rgba(0,0,0,0.6)]" />
          <h2 className="max-w-3xl font-serif text-4xl leading-tight tracking-tight text-[#F5ECDA] md:text-6xl">
            {tr(T.cta.title)}
          </h2>
          <p className="max-w-md text-base text-[#B7A78C]">{tr(T.cta.sub)}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 rounded-full bg-[#D4AF37] px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-black transition-colors hover:bg-[#F3E5AB]" data-testid="footer-whatsapp-cta">
              <WhatsAppIcon className="h-4 w-4" /> {tr(T.nav.book)}
            </a>
            <a href={`tel:${brand.tel}`} className="inline-flex items-center gap-2.5 rounded-full border border-white/20 px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-[#F5ECDA] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="footer-call-cta">
              <Phone className="h-4 w-4" /> {brand.phoneDisplay}
            </a>
          </div>
        </motion.div>

        <div className="flex flex-col gap-8 py-12 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="font-serif text-2xl tracking-[0.25em] text-[#F5ECDA]">SLALY</span>
            <p className="mt-2 text-sm text-[#B7A78C]">{tr(T.footer.tagline)}</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="hidden sm:block"><BookingQR compact /></div>
            <div className="flex items-center gap-4">
            <a href={brand.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#B7A78C] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="social-instagram">
              <Instagram className="h-5 w-5" />
            </a>
            <a href={brand.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#B7A78C] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="social-facebook">
              <Facebook className="h-5 w-5" />
            </a>
            <a href={brand.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#B7A78C] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="social-tiktok">
              <TikTokIcon className="h-5 w-5" />
            </a>
            <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#B7A78C] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="social-whatsapp">
              <WhatsAppIcon className="h-5 w-5" />
            </a>
          </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-white/10 py-8 text-center text-xs text-[#666] md:flex-row md:items-center md:justify-between md:text-left">
          <span>© {year} {brand.full}. {tr(T.footer.rights)}</span>
          <span>{brand.address}</span>
          <a href="/admin" className="inline-flex items-center justify-center gap-1.5 text-[#8a7a5c] transition-colors hover:text-[#D4AF37]" data-testid="footer-admin-link">
            <Lock className="h-3 w-3" /> Admin
          </a>
        </div>
      </div>
    </footer>
  );
};
